import { get } from 'lodash-es';
import { gql } from '@apollo/client';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import {
  createIncentiveAggregationsGqlFilter,
  createInventoryIncentiveListGqlFilter,
} from 'site-modules/shared/utils/incentives-wizard/filter-builder';

const REBATE_ELIGIBILITY_FRAGMENTS = {
  rebateOverridesByEligibilitySegmentFields: `
    fragment wizardRebateOverridesByEligibilityFields on Incentive {
      rebateOverridesByEligibility {
        rebateAmount
        eligibilityFactor {
          ... on EligibilityFactor {
            qualifier
            id
            taxFilingStatus
            maxIncome
            householdSize
            assistanceParticipant
            qualifierDescriptions {
              qualifier
              description
            }
          }
        }
      }
    }
  `,
  topIncentivesByRebateFields: `
    fragment wizardIncentiveFields on Incentive {
      programType
      subtype
      subtypeId
      rebateAmount
      endDate
      restrictions
      id
      type
      transactionTypes
      grantor
      updateDate
      compatibleIncentiveIds
      primary
      programUrl
      ...wizardRebateOverridesByEligibilityFields
    }
  `,
  incentiveAggregationsSegmentFields: `
    fragment wizardIncentiveAggregationsSegmentFields on IncentiveList {
      incentiveAggregations {
        byGrantorAndSubtype(page: $page, sortBy: $sortBy) {
          topIncentiveByRebate {
            ...wizardIncentiveFields
          }
        }
      }
    }
  `,
};

const INCENTIVES_QUERY_BODY = `
  query GetWizardIncentives(
    $incentiveFilters: IncentiveFilters!
    $eligibilityFactors: [EligibilityFactorFilter!]
    $page: PageRequest!
    $sortBy: IncentiveAggregationByGrantorAndSubtypeSort!
  ) {
    incentives(incentiveFilters: $incentiveFilters, eligibilityFactors: $eligibilityFactors) {
      ...wizardIncentiveAggregationsSegmentFields
    }
  }
`;

const INVENTORY_INCENTIVES_LIST_QUERY_BODY = `
  query GetListIncentives(
    $filters: InventoryFilters !
    $incentiveFilters: VinIncentiveFilters!
    $eligibilityFactors: [EligibilityFactorFilter!]
    $page: PageRequest!
    $sortBy: IncentiveAggregationByGrantorAndSubtypeSort!
  ) {
    inventories(filters: $filters) {
      inventories {
        incentiveList(
          vinIncentiveFilters: $incentiveFilters
          eligibilityFactors: $eligibilityFactors
          page: $page
        ) {
          ...wizardIncentiveAggregationsSegmentFields
        }
      }
    }
  }
`;

export const WIZARD_DATA_PATHS = {
  INCENTIVES_INCENTIVE_AGGREGATIONS: 'incentives.incentiveAggregations',
  INVENTORY_INCENTIVE_LIST: 'inventories.inventoryList',
};

const createGqlQuery = queryBody => {
  const allFragments = Object.values(REBATE_ELIGIBILITY_FRAGMENTS).join('\n');
  const fullQueryString = `${allFragments}${queryBody}`;
  const fullQuery = gql(fullQueryString);

  return (context, variables) => withMetrics(EdmundsGraphQLFederation, context).query(fullQuery, variables);
};

export const QUERY_STRATEGIES = {
  [WIZARD_DATA_PATHS.INCENTIVES_INCENTIVE_AGGREGATIONS]: {
    queryFn: createGqlQuery(INCENTIVES_QUERY_BODY),
    getVariables: ({ incentiveFilters, eligibilityFactors }) => ({ incentiveFilters, eligibilityFactors }),
    dataPath: 'incentives.incentiveAggregations.byGrantorAndSubtype',
    createFilterFn: createIncentiveAggregationsGqlFilter,
  },
  [WIZARD_DATA_PATHS.INVENTORY_INCENTIVE_LIST]: {
    queryFn: createGqlQuery(INVENTORY_INCENTIVES_LIST_QUERY_BODY),
    getVariables: ({ vin, incentiveFilters, eligibilityFactors }) => ({
      filters: { vins: [vin] },
      incentiveFilters,
      eligibilityFactors,
    }),
    createFilterFn: createInventoryIncentiveListGqlFilter,
    dataPath: 'inventories.inventories[0].incentiveList.incentiveAggregations.byGrantorAndSubtype',
  },
};

export const getGqlIncentivesWizardData = async (context, strategy, params) => {
  const incentiveFilters = strategy.createFilterFn(params.incentiveFilters);

  const variables = {
    page: { pageNum: 1, pageSize: 16 },
    sortBy: { topIncentiveByRebateAmount: 'DESC' },
    ...strategy.getVariables({ ...params, incentiveFilters }),
  };

  return strategy
    .queryFn(context, variables)
    .then(apiResponse =>
      get(apiResponse, strategy.dataPath, []).map(incentive => get(incentive, 'topIncentiveByRebate'))
    );
};
